/**
 * CapabilityAnalyticsView — the Team Assessment Analytics screen.
 *
 * Pure presentational component: every bit of data, every loading flag, and
 * every callback is supplied by the parent (CapabilityMetricsPage). The
 * parent still owns all state and side-effects (API calls, clipboard, file
 * downloads); this component just renders.
 *
 * This extraction is deliberately mechanical — no logic changes, no new
 * abstractions — so the refactor is safe to ship without deep regression
 * testing.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Loader2, Download, FileText, Link2, Mail, Trophy, TrendingDown,
  Users, Target, BarChart3, Award, Bell, AlertTriangle, ShieldCheck, ShieldX,
  CalendarDays, Clock, Copy, ExternalLink, Trash2,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer,
  LineChart, Line,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { DomainSparkline } from './DomainSparkline';
import { ShareEmailDialog } from './ShareEmailDialog';

const COLORS = ['#002FA7', '#34C759', '#FF9500', '#FF3B30', '#AF52DE'];
const MATURITY_COLORS = { Advanced: '#34C759', Intermediate: '#007AFF', Developing: '#FF9500', Initial: '#FF3B30' };

const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
  catch { toast.error('Copy failed'); }
};

export const CapabilityAnalyticsView = ({
  // Data
  analytics,
  analyticsDomain,
  effectiveness,
  certSchedule,
  certScheduleSaving,
  certFilter,
  allCerts,
  // UI flags
  downloadingAnalytics,
  shareDialogOpen,
  shareType,
  sharing,
  publicLinkDialogOpen,
  publicLinkExpiry,
  creatingPublicLink,
  lastPublicLink,
  myPublicLinks,
  loadingPublicLinks,
  // Config
  certDownloadBaseUrl,
  // Setters (surfaced directly — harmless primitives)
  setView,
  setShareDialogOpen,
  setPublicLinkDialogOpen,
  setPublicLinkExpiry,
  // Handlers
  onDownloadAnalytics,
  onOpenPublicLinkDialog,
  onOpenShareAnalytics,
  onChangeDomain,
  onSaveCertSchedule,
  onFilterCerts,
  onRunExpiryScan,
  onCertReview,
  onShareSend,
  onCreatePublicLink,
  onLoadMyPublicLinks,
  onRevokePublicLink,
}) => {
  const radarData = Object.entries(analytics.section_averages || {})
    .map(([name, value]) => ({ subject: name, score: value, fullMark: 100 }));
  const maturityData = Object.entries(analytics.maturity_distribution || {})
    .map(([name, value]) => ({ name, value }));
  const barData = Object.entries(analytics.section_averages || {})
    .map(([name, value]) => ({ name, score: value }));

  return (
    <div data-testid="assessment-analytics">
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <Button variant="ghost" onClick={() => setView('list')} data-testid="back-from-analytics"><ChevronLeft className="w-4 h-4 mr-1" />Back</Button>
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Team Assessment Analytics</h1>
          <p className="text-[#4B5563] text-sm">Aggregate KM assessment results across your organization</p>
        </div>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <Button variant="outline" size="sm" onClick={() => onDownloadAnalytics('csv')} disabled={downloadingAnalytics === 'csv'} data-testid="analytics-download-csv-btn">
            {downloadingAnalytics === 'csv' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownloadAnalytics('pdf')} disabled={downloadingAnalytics === 'pdf'} data-testid="analytics-download-pdf-btn">
            {downloadingAnalytics === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />}
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onOpenPublicLinkDialog} data-testid="analytics-public-link-btn">
            <Link2 className="w-4 h-4 mr-1" /> Public Link
          </Button>
          <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={onOpenShareAnalytics} data-testid="analytics-share-email-btn">
            <Mail className="w-4 h-4 mr-1" /> Email Share
          </Button>
        </div>
      </div>

      {/* Domain filter row */}
      {(analytics.domain_breakdown && analytics.domain_breakdown.length > 0) && (
        <div className="flex items-center gap-3 mb-4 flex-wrap p-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg" data-testid="domain-filter-row">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-[#4B5563] uppercase tracking-wider">Group by email domain</span>
            <select
              value={analyticsDomain}
              onChange={e => onChangeDomain(e.target.value)}
              className="h-8 border border-[#E5E7EB] rounded-md px-2 text-[12px] bg-white"
              data-testid="domain-filter-select"
            >
              <option value="">All domains ({analytics.domain_breakdown.reduce((s, d) => s + d.count, 0)} submissions)</option>
              {analytics.domain_breakdown.map(d => (
                <option key={d.domain} value={d.domain}>{d.domain} · {d.count} · avg {d.avg_score}%</option>
              ))}
            </select>
          </div>
          {analyticsDomain && (
            <>
              <Badge className="bg-[#002FA7]/10 text-[#002FA7] border-0 text-[10px]" data-testid="domain-filter-active-badge">
                Filtered to @{analyticsDomain}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => onChangeDomain('')} className="h-7 text-[11px]" data-testid="clear-domain-filter-btn">
                Clear filter
              </Button>
            </>
          )}
        </div>
      )}

      {/* Executive insight callout — top vs bottom performing domain */}
      {analytics.domain_insight && !analyticsDomain && (() => {
        const ins = analytics.domain_insight;
        const sev = ins.severity;
        const cfg = sev === 'critical'
          ? { bg: 'bg-gradient-to-r from-[#FEF2F2] to-[#FFF1F1]', border: 'border-[#FF3B30]/30', icon: 'text-[#FF3B30]', accent: 'text-[#FF3B30]', emoji: '🚨', label: 'Critical performance gap' }
          : sev === 'warning'
            ? { bg: 'bg-gradient-to-r from-[#FFFBEB] to-[#FFF7ED]', border: 'border-[#FF9500]/30', icon: 'text-[#FF9500]', accent: 'text-[#FF9500]', emoji: '⚠️', label: 'Performance gap detected' }
            : { bg: 'bg-gradient-to-r from-[#EFF6FF] to-[#F0F9FF]', border: 'border-[#002FA7]/20', icon: 'text-[#002FA7]', accent: 'text-[#002FA7]', emoji: '💡', label: 'Domain insight' };
        const byDomain = Object.fromEntries((analytics.domain_breakdown || []).map(d => [d.domain, d]));
        const topT = byDomain[ins.top_domain]?.trend;
        const botT = byDomain[ins.bottom_domain]?.trend;
        const renderTrend = (t) => {
          if (!t) return null;
          const glyph = t.direction === 'up' ? '↑' : t.direction === 'down' ? '↓' : '→';
          const color = t.direction === 'up' ? 'text-[#34C759]' : t.direction === 'down' ? 'text-[#FF3B30]' : 'text-[#9CA3AF]';
          return (
            <span className={`ml-1 text-[11px] font-semibold ${color}`} title={`Current 7d ${t.current_avg}% vs prior 7d ${t.prior_avg}%`}>
              {glyph}{t.delta_pts > 0 ? '+' : ''}{t.delta_pts}pts w/w
            </span>
          );
        };
        return (
          <div className={`${cfg.bg} ${cfg.border} border rounded-lg p-4 mb-6 flex items-center gap-4 flex-wrap`} data-testid="domain-insight-callout">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-xl" aria-hidden="true">{cfg.emoji}</span>
              <div className="min-w-0">
                <p className={`text-[10px] uppercase tracking-wider font-semibold ${cfg.accent}`}>{cfg.label}</p>
                <p className="text-sm text-[#0A0A0A] mt-0.5" data-testid="domain-insight-text">
                  <Trophy className={`w-4 h-4 inline mr-1 ${cfg.icon}`} />
                  <strong>@{ins.top_domain}</strong> leads at <strong className="text-[#34C759]">{ins.top_avg}%</strong>
                  {renderTrend(topT)}
                  <span className="text-[#9CA3AF]"> ({ins.top_count} subs)</span>
                  <span className="mx-2 text-[#9CA3AF]">·</span>
                  <TrendingDown className={`w-4 h-4 inline mr-1 ${cfg.icon}`} />
                  <strong>@{ins.bottom_domain}</strong> trails at <strong className={cfg.accent}>{ins.bottom_avg}%</strong>
                  {renderTrend(botT)}
                  <span className="text-[#9CA3AF]"> ({ins.bottom_count} subs)</span>
                  <span className="mx-2 text-[#9CA3AF]">·</span>
                  <strong className={cfg.accent}>{ins.gap_pts}-pt gap</strong>
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onChangeDomain(ins.bottom_domain)} data-testid="drill-bottom-domain-btn">
                Drill into @{ins.bottom_domain}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onChangeDomain(ins.top_domain)} data-testid="drill-top-domain-btn">
                View @{ins.top_domain}
              </Button>
            </div>
          </div>
        );
      })()}

      {/* Per-domain breakdown card (only when ≥2 domains exist) */}
      {(analytics.domain_breakdown && analytics.domain_breakdown.length >= 2) && (
        <Card className="border-[#E5E7EB] mb-6" data-testid="domain-breakdown-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Performance by Email Domain</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <tr className="text-left">
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Email domain</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Submissions</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Avg score</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right" title="Current 7 days vs prior 7 days — requires ≥2 submissions in each window">Trend (7d)</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-center" title="Avg score per week over the last 8 weeks">8w sparkline</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Top score</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Drill down</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.domain_breakdown.map(d => {
                    const t = d.trend;
                    const arrowColor = t?.direction === 'up' ? '#34C759' : t?.direction === 'down' ? '#FF3B30' : '#9CA3AF';
                    const arrowGlyph = t?.direction === 'up' ? '↑' : t?.direction === 'down' ? '↓' : '→';
                    return (
                      <tr key={d.domain} className={`border-b border-[#E5E7EB] ${analyticsDomain === d.domain ? 'bg-[#EFF6FF]' : 'hover:bg-[#F9FAFB]'}`} data-testid={`domain-row-${d.domain}`}>
                        <td className="px-4 py-2 font-medium text-[#0A0A0A]">@{d.domain}</td>
                        <td className="px-4 py-2 text-right">{d.count}</td>
                        <td className={`px-4 py-2 text-right font-semibold ${d.avg_score >= 80 ? 'text-[#34C759]' : d.avg_score >= 60 ? 'text-[#FF9500]' : 'text-[#FF3B30]'}`}>{d.avg_score}%</td>
                        <td className="px-4 py-2 text-right" data-testid={`trend-${d.domain}`}>
                          {t ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: arrowColor }} title={`Current avg ${t.current_avg}% (n=${t.current_n}) · Prior 7d ${t.prior_avg}% (n=${t.prior_n})`}>
                              <span aria-hidden="true">{arrowGlyph}</span>
                              {t.delta_pts > 0 ? '+' : ''}{t.delta_pts}pts
                            </span>
                          ) : (
                            <span className="text-[10px] text-[#9CA3AF]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center" data-testid={`sparkline-${d.domain}`}>
                          <DomainSparkline series={d.weekly_series || []} />
                        </td>
                        <td className="px-4 py-2 text-right text-[#002FA7] font-semibold">{d.top_score}%</td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            variant="ghost" size="sm" className="h-7 text-[11px]"
                            onClick={() => onChangeDomain(d.domain)}
                            disabled={analyticsDomain === d.domain}
                            data-testid={`drill-domain-${d.domain}`}
                          >
                            {analyticsDomain === d.domain ? 'Viewing' : 'Drill →'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#002FA7]/10 flex items-center justify-center"><Users className="w-6 h-6 text-[#002FA7]" /></div>
          <div><p className="text-2xl font-bold text-[#0A0A0A]">{analytics.total_submissions}</p><p className="text-xs text-[#9CA3AF]">Total Submissions</p></div>
        </CardContent></Card>
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#34C759]/10 flex items-center justify-center"><Target className="w-6 h-6 text-[#34C759]" /></div>
          <div><p className="text-2xl font-bold text-[#0A0A0A]">{analytics.avg_score}%</p><p className="text-xs text-[#9CA3AF]">Average Score</p></div>
        </CardContent></Card>
        {Object.entries(analytics.section_averages || {}).map(([name, val], i) => (
          <Card key={name} className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${COLORS[i + 2]}15` }}>
              <BarChart3 className="w-6 h-6" style={{ color: COLORS[i + 2] }} /></div>
            <div><p className="text-2xl font-bold text-[#0A0A0A]">{val}%</p><p className="text-xs text-[#9CA3AF]">{name}</p></div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border-[#E5E7EB]"><CardHeader><CardTitle className="text-base">Section Performance Radar</CardTitle></CardHeader>
          <CardContent>{radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}><PolarGrid stroke="#E5E7EB" /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#4B5563' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name="Avg Score" dataKey="score" stroke="#002FA7" fill="#002FA7" fillOpacity={0.25} strokeWidth={2} /></RadarChart>
            </ResponsiveContainer>) : <div className="flex items-center justify-center h-[300px] text-[#9CA3AF] text-sm">No data</div>}
          </CardContent></Card>

        <Card className="border-[#E5E7EB]"><CardHeader><CardTitle className="text-base">Maturity Level Distribution</CardTitle></CardHeader>
          <CardContent>{maturityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart><Pie data={maturityData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                {maturityData.map((entry) => <Cell key={entry.name} fill={MATURITY_COLORS[entry.name] || '#9CA3AF'} />)}
              </Pie><Legend /><ReTooltip /></PieChart>
            </ResponsiveContainer>) : <div className="flex items-center justify-center h-[300px] text-[#9CA3AF] text-sm">No data</div>}
          </CardContent></Card>
      </div>

      <Card className="border-[#E5E7EB] mb-6"><CardHeader><CardTitle className="text-base">Section Average Scores</CardTitle></CardHeader>
        <CardContent>{barData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#4B5563' }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <ReTooltip formatter={(value) => [`${value}%`, 'Score']} />
            <Bar dataKey="score" radius={[6, 6, 0, 0]}>{barData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart></ResponsiveContainer>
        ) : <div className="flex items-center justify-center h-[250px] text-[#9CA3AF] text-sm">No data</div>}
        </CardContent></Card>

      <Card className="border-[#E5E7EB]"><CardHeader><CardTitle className="text-base flex items-center gap-2"><Award className="w-5 h-5 text-[#FF9500]" /> Leaderboard</CardTitle></CardHeader>
        <CardContent className="p-0"><div className="divide-y divide-[#E5E7EB]">
          {(analytics.leaderboard || []).map((entry, idx) => (
            <div key={idx} className="flex items-center gap-4 px-4 py-3" data-testid={`leaderboard-${idx}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${idx === 0 ? 'bg-[#FFD60A] text-[#0A0A0A]' : idx === 1 ? 'bg-[#C0C0C0] text-[#0A0A0A]' : idx === 2 ? 'bg-[#CD7F32] text-white' : 'bg-[#F3F4F6] text-[#4B5563]'}`}>{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0A0A0A]">{entry.user_name}</p>
                {entry.user_email && <p className="text-[11px] text-[#6B7280] truncate" data-testid={`leaderboard-email-${idx}`}>{entry.user_email}</p>}
                <p className="text-[10px] text-[#9CA3AF]">{format(new Date(entry.created_at), 'MMM d, yyyy')}</p>
              </div>
              <Badge className={`text-xs ${entry.maturity_level === 'Advanced' ? 'bg-[#34C759] text-white' : entry.maturity_level === 'Intermediate' ? 'bg-[#007AFF] text-white' : entry.maturity_level === 'Developing' ? 'bg-[#FF9500] text-white' : 'bg-[#FF3B30] text-white'}`}>{entry.maturity_level}</Badge>
              <span className="text-lg font-bold text-[#002FA7]">{entry.overall_score}%</span>
            </div>
          ))}
        </div></CardContent></Card>

      {/* Reminder Effectiveness Panel */}
      {effectiveness && (
        <Card className="border-[#E5E7EB] mt-6" data-testid="reminder-effectiveness-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#002FA7]" />Reminder Effectiveness
                <Badge className={`text-[9px] ml-1 ${
                  effectiveness.trend === 'improving' ? 'bg-[#34C759]/15 text-[#34C759]'
                  : effectiveness.trend === 'declining' ? 'bg-[#FF3B30]/15 text-[#FF3B30]'
                  : 'bg-[#9CA3AF]/15 text-[#9CA3AF]'
                }`} data-testid="effectiveness-trend-badge">
                  {effectiveness.trend === 'improving' && '↑ Improving'}
                  {effectiveness.trend === 'declining' && '↓ Declining'}
                  {effectiveness.trend === 'flat' && '→ Flat'}
                </Badge>
              </CardTitle>
              <span className="text-[10px] text-[#9CA3AF]">Last {effectiveness.days} days</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-3 mb-3">
              <div className="p-3 bg-[#FAFAFA] rounded-lg" data-testid="effectiveness-reminders-count">
                <p className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Reminders sent</p>
                <p className="text-2xl font-bold text-[#002FA7] mt-0.5">{effectiveness.totals.reminders}</p>
              </div>
              <div className="p-3 bg-[#FAFAFA] rounded-lg" data-testid="effectiveness-submissions-count">
                <p className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">New submissions</p>
                <p className="text-2xl font-bold text-[#34C759] mt-0.5">{effectiveness.totals.submissions}</p>
              </div>
              <div className="p-3 bg-[#FAFAFA] rounded-lg" data-testid="effectiveness-conversion">
                <p className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">Conversion rate</p>
                <p className="text-2xl font-bold text-[#0A0A0A] mt-0.5">{effectiveness.totals.conversion_rate}%</p>
                {effectiveness.prior_conversion !== null && effectiveness.recent_conversion !== null && (
                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">
                    last 7d: <span className="font-semibold text-[#0A0A0A]">{effectiveness.recent_conversion}%</span> vs prior 7d: {effectiveness.prior_conversion}%
                  </p>
                )}
              </div>
            </div>
            <div className="h-40" data-testid="effectiveness-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={effectiveness.daily} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9CA3AF' }} tickFormatter={v => v.slice(5)} interval={Math.ceil(effectiveness.daily.length / 10)} />
                  <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} allowDecimals={false} />
                  <ReTooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                  <Line type="monotone" dataKey="reminders" stroke="#002FA7" strokeWidth={2} dot={false} name="Reminders sent" />
                  <Line type="monotone" dataKey="submissions" stroke="#34C759" strokeWidth={2} dot={false} name="New submissions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-2 flex items-center gap-3">
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#002FA7]" />Reminders</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#34C759]" />Submissions</span>
              {effectiveness.trend === 'declining' && (
                <span className="ml-auto text-[#FF3B30] flex items-center gap-1">
                  <AlertTriangle className="w-2.5 h-2.5" />
                  Reminder conversion dropped &gt;10% — consider tuning interval_days or kickoff copy
                </span>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* HR Certification Review Panel */}
      <Card className="border-[#E5E7EB] mt-6" data-testid="cert-review-panel">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#002FA7]" />Certification Verification & Expiry Queue
              {certSchedule?.enabled ? (
                <Badge className="text-[9px] bg-[#34C759]/15 text-[#34C759] ml-1" data-testid="cert-cron-badge">Daily cron @ {String(certSchedule.hour).padStart(2, '0')}:00 UTC</Badge>
              ) : (
                <Badge className="text-[9px] bg-[#9CA3AF]/15 text-[#9CA3AF] ml-1" data-testid="cert-cron-badge">Cron paused</Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex gap-1">
                {['', 'pending', 'verified', 'rejected'].map(f => (
                  <button
                    key={f}
                    onClick={() => onFilterCerts(f)}
                    className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${certFilter === f ? 'bg-[#002FA7] text-white' : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'}`}
                    data-testid={`cert-filter-${f || 'all'}`}
                  >
                    {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => { window.location.href = '/certifications/pipeline'; }} data-testid="open-pipeline-btn">
                <CalendarDays className="w-3 h-3 mr-1" />Renewal Pipeline
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={onRunExpiryScan} data-testid="run-expiry-scan-btn">
                <Bell className="w-3 h-3 mr-1" />Run Expiry Scan
              </Button>
            </div>
          </div>
          {/* Daily scan schedule controls */}
          <div className="flex items-center gap-3 pt-2 mt-2 border-t border-[#E5E7EB] flex-wrap">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!certSchedule?.enabled}
                onChange={e => onSaveCertSchedule({ enabled: e.target.checked })}
                disabled={certScheduleSaving}
                className="w-3.5 h-3.5 accent-[#002FA7]"
                data-testid="cert-cron-enabled-toggle"
              />
              <span className="text-[11px] text-[#4B5563]">Daily auto-scan</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-[#9CA3AF]">at</span>
              <select
                value={certSchedule?.hour ?? 8}
                onChange={e => onSaveCertSchedule({ hour: parseInt(e.target.value, 10) })}
                disabled={certScheduleSaving}
                className="h-6 text-[11px] border border-[#E5E7EB] rounded px-1"
                data-testid="cert-cron-hour-select"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>
                ))}
              </select>
            </div>
            <span className="text-[10px] text-[#9CA3AF]" data-testid="cert-cron-next-run">
              {certSchedule?.enabled && certSchedule?.next_run_at
                ? `Next: ${format(new Date(certSchedule.next_run_at), "MMM d, HH:mm 'UTC'")}`
                : 'Next: paused'}
            </span>
            {certSchedule?.last_run_at && (
              <span className="text-[10px] text-[#9CA3AF]" data-testid="cert-cron-last-run">
                Last: {format(new Date(certSchedule.last_run_at), 'MMM d, HH:mm')} · {certSchedule.last_run_triggered_by || '—'}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {allCerts.length === 0 ? (
            <div className="text-center py-6 text-[#9CA3AF] text-sm">
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
              <p>{certFilter ? `No ${certFilter} certifications` : 'No certifications uploaded yet'}</p>
              <Button size="sm" variant="outline" className="mt-2 text-xs" onClick={() => onFilterCerts(certFilter)} data-testid="load-certs-btn">Load Certifications</Button>
            </div>
          ) : (
            <div className="space-y-2">
              {allCerts.map(c => {
                const sColors = { pending: '#FF9500', verified: '#34C759', rejected: '#FF3B30' };
                const exColors = { expired: '#FF3B30', critical: '#FF3B30', expiring_soon: '#FF9500', expiring: '#FFCC00', valid: '#34C759', none: '#9CA3AF' };
                const d = c.days_until_expiry;
                let exLabel = null;
                if (c.expiry_date && (d !== null && d !== undefined)) {
                  exLabel = d < 0 ? `Expired ${-d}d ago` : d === 0 ? 'Expires today' : `Expires in ${d}d`;
                }
                return (
                  <div key={c.cert_id} className="flex items-center justify-between p-3 rounded-lg border border-[#E5E7EB]" data-testid={`review-cert-${c.cert_id}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Badge className="text-[8px] flex-shrink-0" style={{ backgroundColor: sColors[c.status] + '15', color: sColors[c.status] }}>{c.status}</Badge>
                      {exLabel && (
                        <Badge className="text-[8px] flex-shrink-0 flex items-center gap-0.5" style={{ backgroundColor: exColors[c.expiry_status] + '15', color: exColors[c.expiry_status] }} data-testid={`hr-expiry-badge-${c.cert_id}`}>
                          <Clock className="w-2.5 h-2.5" />{exLabel}
                        </Badge>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A] truncate">{c.certification_name} — <span className="font-normal text-[#4B5563]">{c.user_name}</span></p>
                        <p className="text-[10px] text-[#9CA3AF]">
                          {c.file_name} | {format(new Date(c.created_at), 'MMM d, yyyy')}
                          {c.expiry_date ? ` | Expires ${c.expiry_date}` : ''}
                          {c.reviewer_name ? ` | Reviewed by ${c.reviewer_name}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a href={`${certDownloadBaseUrl}/${c.cert_id}`} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-[#F3F4F6]" title="Download">
                        <Download className="w-3.5 h-3.5 text-[#007AFF]" />
                      </a>
                      {c.status === 'pending' && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-[#34C759]" onClick={() => onCertReview(c.cert_id, 'verified')} data-testid={`verify-${c.cert_id}`}>
                            <ShieldCheck className="w-3 h-3 mr-0.5" />Verify
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-[#FF3B30]" onClick={() => onCertReview(c.cert_id, 'rejected', 'Certificate could not be verified')} data-testid={`reject-${c.cert_id}`}>
                            <ShieldX className="w-3 h-3 mr-0.5" />Reject
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ShareEmailDialog
        open={shareDialogOpen && shareType === 'analytics'}
        onClose={setShareDialogOpen}
        title="Share Team Assessment Analytics via Email"
        onSend={onShareSend}
        sending={sharing}
      />

      <Dialog open={publicLinkDialogOpen} onOpenChange={setPublicLinkDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="public-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#002FA7]" /> Share via Public Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-[#4B5563]">
              Generate a tokenized, read-only snapshot URL that anyone can open — no KStreak account required.
              Numbers are frozen at the moment of creation and stay stable as new submissions arrive.
            </p>

            <div>
              <Label className="text-xs font-semibold">Expiry</Label>
              <div className="flex gap-2 mt-1.5" data-testid="public-link-expiry-group">
                {[1, 7, 30, 90].map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setPublicLinkExpiry(d)}
                    className={`flex-1 h-9 rounded-md border text-xs font-medium transition-colors ${
                      publicLinkExpiry === d
                        ? 'bg-[#002FA7] text-white border-[#002FA7]'
                        : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'
                    }`}
                    data-testid={`public-link-expiry-${d}`}
                  >
                    {d} day{d === 1 ? '' : 's'}
                  </button>
                ))}
              </div>
            </div>

            <Button
              className="w-full bg-[#002FA7] hover:bg-[#00227A]"
              onClick={onCreatePublicLink}
              disabled={creatingPublicLink}
              data-testid="create-public-link-btn"
            >
              {creatingPublicLink ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
              Generate Link
            </Button>

            {lastPublicLink && (
              <div className="p-3 bg-[#F0FDF4] border border-[#34C759]/30 rounded-md space-y-2" data-testid="new-public-link-block">
                <p className="text-[11px] text-[#166534] font-semibold">Link generated — copied to clipboard</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={lastPublicLink.public_url} className="text-[11px] font-mono bg-white" data-testid="new-public-link-url" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(lastPublicLink.public_url)} data-testid="copy-public-link-btn">
                    <Copy className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(lastPublicLink.public_url, '_blank')} data-testid="open-public-link-btn">
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-[10px] text-[#4B5563]">
                  Expires {lastPublicLink.expires_at ? format(new Date(lastPublicLink.expires_at), 'MMM d, yyyy HH:mm') : '—'} UTC
                </p>
              </div>
            )}

            <div className="pt-2 border-t border-[#F3F4F6]">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">My active snapshots</Label>
                <Button variant="ghost" size="sm" onClick={onLoadMyPublicLinks} className="h-6 text-[10px]" data-testid="refresh-public-links-btn">
                  Refresh
                </Button>
              </div>
              {loadingPublicLinks ? (
                <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#9CA3AF]" /></div>
              ) : myPublicLinks.length === 0 ? (
                <p className="text-[11px] text-[#9CA3AF] text-center py-3" data-testid="no-public-links">No public snapshots yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="public-links-list">
                  {myPublicLinks.map(link => (
                    <div
                      key={link.snapshot_id}
                      className={`p-2 border rounded-md flex items-center gap-2 text-[11px] ${link.revoked ? 'bg-[#FEF2F2] border-[#FECACA]' : link.expired ? 'bg-[#FFFBEB] border-[#FDE68A]' : 'bg-white border-[#E5E7EB]'}`}
                      data-testid={`public-link-row-${link.snapshot_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] text-[#4B5563] truncate">{link.public_url}</p>
                        <p className="text-[#9CA3AF] text-[10px]">
                          {link.revoked ? 'Revoked' : link.expired ? 'Expired' : `Expires ${format(new Date(link.expires_at), 'MMM d, HH:mm')}`} ·
                          <span className="ml-1">{link.views} view{link.views === 1 ? '' : 's'}</span>
                        </p>
                      </div>
                      {!link.revoked && !link.expired && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => copyToClipboard(link.public_url)} data-testid={`copy-link-${link.snapshot_id}`}>
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#FF3B30]" onClick={() => onRevokePublicLink(link.snapshot_id)} data-testid={`revoke-link-${link.snapshot_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
